import { Location } from 'history';
import { useRouteMatch } from 'react-router-dom';
import { rootMenuQuery } from '../queries/ats/layout/queries';
import { PlayOptions } from '../queries/ats/playback/api';
import { SingleRecording } from '../queries/ats/recordings/types/recording';
import { queryClient } from '../queries/client';

export function usePathTo<T extends string>(to: T, overridePath?: string) {
  const { path } = useRouteMatch();
  const pathToUse = overridePath ? overridePath : path;
  return pathToUse + to;
}

export type UnmanagedRouteActions = 'my-recordings';
interface UnmanagedRoutesOptions {
  base: string;
}

export class UnmanagedRoutes {
  public base: string;

  private cache = new Map<UnmanagedRouteActions, string>();

  constructor(options: UnmanagedRoutesOptions) {
    this.base = options.base;
    this.populateCache();
  }

  public get(action: UnmanagedRouteActions): string {
    const entry = this.cache.get(action);

    if (!entry) {
      throw new Error(`Path for ${action} not found in cache`);
    }

    return entry;
  }

  private async populateCache() {
    const data = await queryClient.fetchQuery(rootMenuQuery());

    for (const action of ['my-recordings'] as const) {
      const unmanagedItem = data.find((item) => item.type === 'unmanaged' && item.params.action === action);

      if (unmanagedItem) {
        this.cache.set(action, unmanagedItem.slug);
      }
    }
  }
}

interface PlayRouteOptions extends Omit<PlayOptions, 'startSession' | 'isTrailer'> {
  position?: number;
}

interface PlayLiveOptions {
  channelId: string;
  position?: number;
}

class Routes {
  private unmanagedRoutes = new UnmanagedRoutes({ base: '/page' });

  public to(...paths: string[]) {
    return new URL(`${paths.filter(Boolean).join('/')}`, window.location.origin);
  }

  public unmanaged(path: string) {
    const paths = path.split('/');
    return this.to(this.unmanagedRoutes.base, ...paths);
  }

  public play(options: PlayRouteOptions) {
    const url = this.to(this.unmanagedRoutes.base, 'play', options.platformTitleId);

    if (options.providerIdentifier !== undefined) {
      url.searchParams.append('provider', options.providerIdentifier);
    }

    if (options.position !== undefined) {
      url.searchParams.append('position', Math.round(options.position).toString());
    }

    if (options.masterId !== undefined) {
      url.searchParams.append('masterId', options.masterId);
    }
    return url;
  }

  public live(options: PlayLiveOptions) {
    const url = this.to(this.unmanagedRoutes.base, 'tv', options.channelId);
    if (options.position !== undefined) {
      url.searchParams.append('position', Math.round(options.position).toString());
    }
    return url;
  }

  public trailer(platformTitleId: string, providerIdentifier: string) {
    return this.to(this.unmanagedRoutes.base, 'play', 'trailer', platformTitleId, providerIdentifier);
  }

  public login(location: Location<unknown>) {
    const url = this.to('auth', 'login');
    url.searchParams.append('redirect', location.pathname);
    return url;
  }

  public recording(recording: SingleRecording, position?: number) {
    const path = this.unmanagedRoutes.get('my-recordings');
    const pvrId = recording.id;

    const url = this.to(this.unmanagedRoutes.base, path, 'play', pvrId);

    if (position !== undefined) {
      url.searchParams.set('position', position.toString());
    }

    return url;
  }

  public auth(redirect: string) {
    const url = this.to('auth', 'login');
    url.searchParams.set('redirect', redirect);
    return url;
  }
}

export const routes = new Routes();
